import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Dialog } from '@capacitor/dialog';

import {
  useAppleLoginMutation,
  useLinkAppleMutation,
} from '@/api/queries/auth';
import { AppRoute } from '@/common/app/routes';
import { useUnregisterPush } from '@/common/hooks/push-notifications';
import {
  type AppleCredentials,
  authorizeWithApple,
} from '@/common/lib/apple-sign-in';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { setToken } from '@/redux/reducers/userReducer';
import { useAppDispatch } from '@/redux/store';

const isConflict = (e: unknown): boolean =>
  (e as { status?: number }).status === 409;

/**
 * Sign in with Apple of the iOS app. `login` restores (or starts) the game of
 * an Apple ID; `link` attaches the Apple ID to the current guest game, and
 * when that Apple ID already has a game of its own, offers to switch to it.
 */
const useAppleAuth = (): {
  isLoading: boolean;
  login: () => Promise<void>;
  link: () => Promise<void>;
} => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const unregisterPush = useUnregisterPush();
  const [appleLogin] = useAppleLoginMutation();
  const [linkApple] = useLinkAppleMutation();
  const [isLoading, setIsLoading] = useState(false);

  const signIn = async (credentials: AppleCredentials): Promise<void> => {
    // Token goes to the store via userReducer (appleLogin.matchFulfilled)
    const user = await appleLogin(credentials).unwrap();

    if (!user.token) throw new Error('No session token');
    dispatch(setToken(user.token));
    localStorage.setItem('auth_token', user.token);
  };

  const run = async (action: () => Promise<void>): Promise<void> => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      await action();
    } catch {
      showToast(t('Something went wrong, please try again'));
    } finally {
      setIsLoading(false);
    }
  };

  const login = (): Promise<void> =>
    run(async () => {
      const credentials = await authorizeWithApple();

      if (!credentials) return;
      await signIn(credentials);
      navigate(AppRoute.MAIN);
    });

  const link = (): Promise<void> =>
    run(async () => {
      const credentials = await authorizeWithApple();

      if (!credentials) return;

      try {
        await linkApple(credentials).unwrap();
        showToast(t('Your account is linked to Apple ID'));

        return;
      } catch (e) {
        if (!isConflict(e)) throw e;
      }

      const { value } = await Dialog.confirm({
        title: t('Apple ID already in use'),
        message: t(
          'This Apple ID already has its own game. Switch to it? Your guest progress stays on this device: choose "Play as new player" to come back to it.',
        ),
        okButtonTitle: t('Switch'),
        cancelButtonTitle: t('Cancel'),
      });

      if (!value) return;
      // While still signed in as the guest: its pushes must stop here
      await unregisterPush();
      await signIn(credentials);
      // A different company: start over instead of patching every cache
      window.location.replace('/');
    });

  return { isLoading, login, link };
};

export { useAppleAuth };
